/**
 * `heysegment media` — aggregate image and video helpers.
 *
 * Images and videos keep their specialized upload commands, but agents often
 * just need to rediscover recent asset URLs without remembering which media
 * family they came from. `media list` gives one combined view.
 *
 * @module
 */
import { Console, Effect } from "effect"
import { Command } from "effect/unstable/cli"
import type {
  CliImage,
  CliVideo,
} from "@repo/contracts/rpc/procedures/session"
import { OperationFailed } from "../errors"
import { SessionClient } from "../rpc"
import { withSession, step } from "../session/with-session"
import { renderImageList } from "./images"
import { renderVideoList } from "./videos"
import { apiUrlFlag, dirFlag, jsonFlag } from "./flags"

export const renderMediaList = (input: {
  readonly images: ReadonlyArray<CliImage>
  readonly videos: ReadonlyArray<CliVideo>
}): string => {
  if (input.images.length === 0 && input.videos.length === 0) {
    return [
      "No media in this workspace yet. Add assets with:",
      "  heysegment images upload <file>",
      "  heysegment videos upload <file>",
    ].join("\n")
  }

  const sections: Array<string> = []
  if (input.images.length > 0) {
    sections.push(`Images\n${renderImageList(input.images)}`)
  }
  if (input.videos.length > 0) {
    sections.push(`Videos\n${renderVideoList(input.videos)}`)
  }
  return sections.join("\n\n")
}

const runList = (input: {
  readonly json: boolean
}): Effect.Effect<void, OperationFailed, SessionClient> =>
  Effect.gen(function* () {
    const client = yield* SessionClient
    const [imageResult, videoResult] = yield* Effect.all([
      step("media:images", client.listImages(void 0)),
      step("media:videos", client.listVideos(void 0)),
    ])

    if (input.json) {
      yield* Console.log(
        JSON.stringify(
          { ok: true, images: imageResult.images, videos: videoResult.videos },
          null,
          2,
        ),
      )
      return
    }

    yield* Console.log(
      renderMediaList({ images: imageResult.images, videos: videoResult.videos }),
    )
  })

export const mediaListCommand = Command.make(
  "list",
  { json: jsonFlag, dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      runList({ json: flags.json }),
    ),
).pipe(Command.withDescription("List the workspace's images and videos together"))

export const mediaCommand = Command.make("media").pipe(
  Command.withDescription("List workspace media across images and videos"),
  Command.withSubcommands([mediaListCommand]),
)
